'use client';
import {
  CouponInvalidDialog,
  ExchangeCodeDialog,
  SuccessDialog as SuccessDialogUI,
} from '@wwe-portal/ui/features/store-revamp/dialog';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import axios from 'axios';

import {
  createPortableDialog,
  usePortableDialog,
} from '@wwe-portal/ui/components/dialog/portable-dialog';
import { showDialog } from '@wwe-portal/ui/components/dialog/portable-dialog';

import {
  UnexpectedError,
  APIError,
} from '@wwe-portal/web/services/client/error';

import { useGetCurrencies } from '@wwe-portal/web/providers/queries/offer';
import { useGetUserInfo } from '@wwe-portal/web/providers/queries/user-info';
import { OfferData } from '@wwe-portal/entities/offer.type';
import type {
  RedeemCouponPlaygamiData,
  PlaygamiResolvedItem,
} from '@wwe-portal/entities/offer.type';
import { usePostCoupon } from '@wwe-portal/web/providers/queries/offer.mutation';
import { CouponExistContent } from '../../coupon-code/coupon-exist-content/coupon-exist-content';
import {
  sendRedeemCouponEventError,
  sendRedeemCouponEventSuccess,
} from '@wwe-portal/web/libs/analytics/coupon';
import { offerToCouponProps } from '@wwe-portal/ui/features/offer/lib/mapper';
import wweLogoWebp from '@wwe-portal/ui/assets/images/flagship/wwe-logo.webp';
import {
  Carousel,
  CarouselArrow,
  CarouselContent,
  CarouselItem,
} from '@wwe-portal/ui/components/carousel';

export const SuccessDialog = createPortableDialog(
  ({ items }: { items: OfferData[] }) => {
    const modal = usePortableDialog();
    const offerItems = items.map(offerToCouponProps);
    return (
      <SuccessDialogUI
        open={modal.visible}
        onClose={() => modal.remove()}
        actionElement={<CouponExistContent offerItems={offerItems} />}
      />
    );
  }
);

const PlaygamiRewardCard = ({ item }: { item: PlaygamiResolvedItem }) => {
  const imgSrc = item.images.url || item.images.resizeUrl;
  return (
    <div className="w-[120px] overflow-hidden rounded-[10px] shadow-[0_0_4px_4px_#b375da]">
      <div className="flex h-[90px] items-center justify-center bg-[#1d255f] p-2">
        <img
          src={imgSrc || wweLogoWebp}
          alt={item.itemName}
          className="max-h-[70px] max-w-[70px] object-contain"
        />
      </div>
      <div className="bg-[#272b4a] px-2 py-2 text-center">
        <p className="text-text100 text-xs font-medium">
          {item.quantity > 1 ? `x${item.quantity} ` : ''}
          {item.itemName}
        </p>
      </div>
    </div>
  );
};

export const PlaygamiSuccessDialog = createPortableDialog(
  ({ resolvedItems }: { resolvedItems: PlaygamiResolvedItem[] }) => {
    const modal = usePortableDialog();
    return (
      <SuccessDialogUI
        open={modal.visible}
        onClose={() => modal.remove()}
        actionElement={
          resolvedItems.length > 1 ? (
            <div className="w-[270px]">
              <Carousel
                className="overflow-hidden pr-3"
                opts={{
                  startIndex: 0,
                  loop: true,
                  containScroll: 'trimSnaps',
                }}
              >
                <CarouselContent className="ml-0">
                  {resolvedItems.map((item) => (
                    <CarouselItem key={item.itemID} data-carousel-item>
                      <div className="flex justify-center py-3">
                        <PlaygamiRewardCard item={item} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselArrow />
              </Carousel>
            </div>
          ) : resolvedItems.length === 1 ? (
            <div className="flex justify-center py-3">
              <PlaygamiRewardCard item={resolvedItems[0]!} />
            </div>
          ) : undefined
        }
      />
    );
  }
);

export const InvalidDialog = createPortableDialog(
  ({ message }: { message: string }) => {
    const modal = usePortableDialog();
    return (
      <CouponInvalidDialog
        open={modal.visible}
        onClose={() => modal.remove()}
        errorMessage={message}
      />
    );
  }
);

const PROMO_ERROR_KEYS: Record<string, string> = {
  code_not_found: 'promo_error_code_not_found',
  redemption_invalid: 'promo_error_redemption_invalid',
  reward_expired: 'promo_error_reward_expired',
  user_id_mismatch: 'promo_error_user_id_mismatch',
  redemption_in_progress: 'promo_error_redemption_in_progress',
  code_already_claimed: 'promo_error_code_already_claimed',
  promo_code_limit_exceed: 'promo_error_promo_code_limit_exceed',
};

export const ExchangeDialog = createPortableDialog(() => {
  const modal = usePortableDialog();
  const [value, setValue] = useState('');
  const { data: currenciesResponse } = useGetCurrencies();
  const { data: user } = useGetUserInfo();
  const t = useTranslations();

  const formatErrorMessage = (err: unknown): string => {
    const axiosError =
      err instanceof UnexpectedError && axios.isAxiosError(err.error)
        ? err.error
        : err instanceof APIError && axios.isAxiosError(err.originError)
          ? err.originError
          : null;

    if (axiosError) {
      const status = axiosError.response?.status;
      if (status === 429) return t('promo_error_too_many_requests');
      const rawMessage = axiosError.response?.data?.message;
      if (rawMessage) {
        const translationKey = PROMO_ERROR_KEYS[rawMessage];
        if (translationKey) return t(translationKey);
        return rawMessage;
      }
    }
    return 'Invalid code';
  };

  const { mutate } = usePostCoupon({
    onError: async (err: unknown) => {
      sendRedeemCouponEventError({ userId: user?.userId ?? '' });

      showDialog(InvalidDialog, {
        message: formatErrorMessage(err),
      });
    },
    onSuccess: async (data) => {
      const responseData = data?.data?.data;
      const isPlaygami = responseData?.rewardType === 'playgami';

      if (!isPlaygami && user && currenciesResponse) {
        const xsollaData = responseData as {
          items: OfferData[];
          externalId: string;
        };
        sendRedeemCouponEventSuccess({
          offerDataList: xsollaData?.items || [],
          inputCode: value,
          promotionId: xsollaData?.externalId ?? '',
          currentUser: user,
          currencies: currenciesResponse.data.data,
          ruby: user.ruby,
          webCoin: user.webCoin,
        });
      }
      modal.remove();

      if (isPlaygami) {
        const playgamiData = responseData as RedeemCouponPlaygamiData;
        showDialog(PlaygamiSuccessDialog, {
          resolvedItems: playgamiData.resolvedItems,
        });
      } else {
        const xsollaData = responseData as { items: OfferData[] };
        showDialog(SuccessDialog, { items: xsollaData?.items });
      }
    },
  });

  return (
    <ExchangeCodeDialog
      open={modal.visible}
      onClose={() => modal.remove()}
      isSubmitting={false}
      onSubmit={() => {
        mutate({
          couponId: value,
        });
      }}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value);
      }}
      value={value}
    />
  );
});
